import {HostListener, Injector, OnInit} from '@angular/core';
import {FormGroup, FormArray, FormControl, FormBuilder, AbstractControl} from '@angular/forms';
import {ApiService} from './api.service';
import {EventsService} from '@services/events.service';
import {FormHelperService} from '@services/form-helper.service';

interface FrontendFormInterface {
    /**
     * Сообщение уведомления об успешном сохранении формы
     */
    afterSaveMessage?: string;
}

export abstract class FrontendFormComponent implements OnInit, FrontendFormInterface {

    public abstract formService: ApiService;

    protected _fb = new FormBuilder();

    public form: FormGroup;

    public isPending = false;

    public requestParams: any = {};

    public afterSaveMessage: string;

    protected eventsService: EventsService;

    protected constructor(
        public injector: Injector
    ) {
        this.eventsService = this.injector.get(EventsService);
    }

    ngOnInit() {
        this.init();
    }

    abstract init(): void;

    /**
     * Вывод сообщения об ошибке в интерфейс
     * @param errors
     */
    showFormErrors(errors) {
        this.resetErrors(this.form);
        this.setGroupErrors(this.form, errors);
        this.showErrorsNotification(errors);
        this.isPending = false;
        this.afterError();
    }

    afterError(): void {

    }

    afterSave(data?): void {
        this.isPending = false;
        if (this.afterSaveMessage) {
            this.eventsService.notyShow$.next({
                type: 'success',
                content: this.afterSaveMessage,
                hideOnRouteChange: false,
                timeout: 5000,
            });
        }
    }

    /**
     * Рекурсивный обход объекта с ошибками и выставление ошибок соответствующим полям формы.
     * Обходит поля и если встречает группу, то рекурсивно проходит и по ней.
     * @param group - текущая группа, для которой выставляются ошибки.
     * @param data - объект с ошибками.
     */
    setGroupErrors(group: FormGroup, data: any) {
        for (const key in data) {
            if (!group.controls.hasOwnProperty(key)) {
                continue;
            }

            // Если значением текущей ошибки является не строка, значит ошибка относится к группе
            // и нужно рекурсивно спуститься в эту группу для обхода.
            if (typeof data[key][0] !== 'string') {
                this.setGroupErrors(<FormGroup>group.controls[key], data[key]);
                continue;
            }

            group.controls[key].setErrors(data[key]);

            /**
             * Делаем поле "Чистым" и навешиваем валидатор с ошибками backend до тех пор, пока пользователь
             * не произведет взаимодействие с полем.
             * Это сделано для того, чтобы поля не сбрасывали свои ошибки при обновлении View, если используется
             * только setErrors, так как в этом случае у них нет валидаторов, форма пересчитает ошибки для View
             * и не обнаружит их
             */
            group.controls[key].markAsPristine();
            group.controls[key].setValidators((input: FormControl) => {
                return input.pristine ? {'frontendRequired': data[key].join()} : null;
            });
        }
    }

    /**
     * @param model
     */
    save(model: FormGroup): void {
        /**
         * Запрещаем сохранение формы если она уже отправляется,
         * если метод запрещает отправку из-за внешних условий,
         * либо если форма вся задизейблена
         */
        if (this.isPending || this.preventSave() || this.form.disabled) {
            return;
        }

        this.beforeSave();

        /**
         * Скрываем все временные уведомления, так как в этот момент они более не нужны.
         * Например, если было 500-ая, то зачем показывать ее в момент попытки сохранения,
         * ведь дальше либо ошибки больше не будет, либо появится внось.
         * Аналогично и с ошибками - если отображалось уведомление с ошибками, то их может больше и не быть.
         */
        this.eventsService.notyHide$.next();

        this.isPending = true;
        /**
         * Берем getRawValue() вместо value, чтобы собрать данные с disabled полей
         * https://angular.io/api/forms/FormGroup#getrawvalue
         * @type {any}
         */
        const modelValue = model.getRawValue();

        this.formService.post({
            url: this.saveUrl,
            dataBody: modelValue,
            queryParams: this.requestParams || {},
        }).subscribe(
            data => this.saveHandler(data),
            error => this.showFormErrors(error),
        );
    }

    /**
     * Сброс ошибок для всех полей формы с использованием повторной валидации
     * @param fg
     */
    resetErrors(fg: FormGroup) {

        if (!fg || !fg.controls) {
            return;
        }

        Object.keys(fg.controls).forEach((key) => {
            const control: AbstractControl = fg.controls[key];

            if (control.errors) {
                FormHelperService.updateWithTouch(control);
            }

            if (typeof control.value === 'object') {
                this.resetErrors(control as FormGroup);
            }
        });
    }

    get saveUrl(): string {
        return this.formService.url;
    }

    /**
     * @param data
     */
    saveHandler(data): any {
        if (data && data instanceof Object) {
            try {
                this.form.patchValue(data);
            } catch (e) {

            }
        }
        this.afterSave(data);
    }

    /**
     * Предотвратить сохранение?
     * @returns {boolean}
     */
    preventSave() {
        return false;
    }

    /**
     * Метод перед сохранением
     */
    beforeSave(): void {

    }

    showErrorsNotification(error) {
        /* Получаем все сообщения об ошибках */
        const errorMessagesArray = this.getErrorMessages(error);
        if (!errorMessagesArray.length) {
            return;
        }

        /* Из ошибок строим DOM сообщения, которое отобразим в уведомлении пользователю */
        const errorMessage = errorMessagesArray.reduce((val, item, i, arr) => {
            return val += `<p>${item}</p>`;
        }, '');

        this.eventsService.notyShow$.next({
            type: 'error',
            content: errorMessage,
        });

        /**
         * Нужно отчекнуть отправку и обновить интерфейс, чтобы формы, находящиеся внутри
         * ChangeDetection.OnPush компонентов корректно обновились сами
         * @type {boolean}
         */
        this.isPending = false;
    }

    /**
     * Метод рекурсивно парсит объект ошибки и возвращает коллекцию сообщений ошибок.
     * @param error
     * @returns {Array} = ['Сообщение 1', 'Сообщение 2', ...]
     */
    protected getErrorMessages(error) {
        let messages = [];

        /**
         * Если ошибка - просто строка текста, то возвращаем коллекцию из этой одной строки
         */
        if (typeof error === 'string') {
            messages.push(error);
            return messages;
        }

        /**
         * Если ошибка массив, то рекурсивно парсим каждый её элемент как самостоятельную ошибку
         */
        if (error instanceof Array) {
            error.forEach(e => {
                messages = messages.concat(this.getErrorMessages(e));
            });
            return messages;
        }

        /**
         * Если ошибка - объект, то рекурсивно парсим значения её ключей
         */
        if (error instanceof Object) {
            for (const key of Object.keys(error)) {
                messages = messages.concat(this.getErrorMessages(error[key]));
            }
            return messages;
        }

        /**
         * По-умолчанию возвращаем пустую коллекцию, если ошибку не удалось распарсить способами выше.
         */
        return messages;
    }

    /**
     * Сброс всей формы
     */
    protected reset() {
        const id = this.form.contains('id') ? this.form.get('id').value : null;

        this.resetHandle(this.form);

        if (id) {
            this.form.get('id').patchValue(id);
        }
    }

    /**
     * Обработчик сброса переданной группы, который рекурсивно сбросит все поля, списки и группы внутри
     * @param {FormGroup} group
     */
    private resetHandle(group: FormGroup) {
        for (const key of Object.keys(group.controls)) {
            const control = group.controls[key];

            if (control instanceof FormControl) {
                /**
                 * Делаем все задизейбленные поля активными.
                 * Если нужно, чтобы поля остались задизйблеными, то это делается в форме
                 * в ручном режиме сразу после сброса.
                 */
                control.enable();
                control.patchValue('');
                control.markAsTouched();
                control.markAsDirty();
                control.updateValueAndValidity();
            }

            if (control instanceof FormArray) {
                while (control.length) {
                    control.removeAt(0);
                }
            }

            if (control instanceof FormGroup) {
                this.resetHandle(control);
            }
        }
    }

    /**
     * Обработчик нажатия Enter в форме
     * @param event
     * @returns {boolean}
     */
    @HostListener('keydown.enter', ['$event'])
    onEnterPress(event) {
        /**
         * Запрещаем срабатывание Enter везде кроме Textarea
         */
        return event.target.tagName === 'TEXTAREA';
    }

    setFormValues(values, group: FormGroup): void {
        FormHelperService.setFormValues(values, group);
    }
}

